import { useCallback } from 'react'

import { ChatParticipant } from '@tribeplatform/gql-client/types'
import { useTransferChatOwnership } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { Form } from '../../Form/index.js'
import { T, useI18n } from '../../i18n/index.js'

import { ModalMemberDetails } from './ModalMemberDetails.js'

interface FormFields {
  stayAdmin: boolean
}

interface Props {
  chatName: string
  open: boolean
  participant: ChatParticipant
  onClose: () => void
}

export const TransferOwnershipModal = ({
  open,
  onClose,
  chatName,
  participant,
}: Props) => {
  const { mutateAsync: transferChatOwnership, isLoading } =
    useTransferChatOwnership()
  const { $t } = useI18n()

  const onSubmit = useCallback(
    async (values: FormFields) => {
      try {
        await transferChatOwnership({
          chatId: participant.chatId,
          input: {
            newOwnerId: participant.memberId,
            stayAdmin: values.stayAdmin,
          },
        })
        toast({
          title: $t({
            id: 'Generics.Success',
            defaultMessage: 'Success',
          }),
          description: $t({
            id: 'Messaging.GroupParticipantsList.TransferOwnership.Success',
            defaultMessage: 'The ownership has been transferred',
          }),
          status: 'success',
        })
        onClose()
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Messaging.GroupParticipantsList.TransferOwnership.Error',
            defaultMessage: 'Failed to transfer ownership',
          }),
          status: 'error',
        })
      }
    },
    [
      $t,
      onClose,
      participant.chatId,
      participant.memberId,
      transferChatOwnership,
    ],
  )

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={
          <div className="flex items-center gap-2">
            <T
              defaultMessage="Transfer Ownership"
              id="Messaging.TransferOwnershipModal.Title"
            />
          </div>
        }
        description={
          <T
            defaultMessage="Transfer ownership of the group chat ({chat_name}) to {member_name}."
            id="Messaging.TransferOwnershipModal.Description"
            values={{
              chat_name: chatName,
              member_name: (
                <span className="font-bold">{participant.name}</span>
              ),
            }}
          />
        }
      />
      <Modal.Content>
        <Form<FormFields>
          className="space-y-6"
          onSubmit={onSubmit}
          defaultValues={{ stayAdmin: true }}
        >
          <>
            <ModalMemberDetails participant={participant} />
            <Form.Checkbox
              name="stayAdmin"
              label={
                <T
                  defaultMessage="Stay as admin"
                  id="Messaging.TransferOwnershipModal.StayAdmin"
                />
              }
              helperText={
                <T
                  defaultMessage="Remain as admin to continue managing the group after the transfer. If unchecked, you'll become a regular member."
                  id="Messaging.TransferOwnershipModal.StayAdminHelperText"
                />
              }
            />
            <Form.Actions>
              <Button destructive type="submit" loading={isLoading}>
                <T defaultMessage="Transfer" id="Generics.Transfer" />
              </Button>
            </Form.Actions>
          </>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
